import type { GridPageChangeEvent } from "@tql/ui-components-react/kendo/grid";
import { useCallback, useEffect, useRef, useState } from "react";

import { useItemsPerPagePreference } from "~/features/profile/hooks/use-items-per-page-preference.ts";
import { GridIdentifiers } from "~/shared/utils/grid-identifier-enums.ts";

type UseExternalPagingOptions = {
	/** Whether external (non-internal) paging is active. */
	enabled: boolean;
	/** Current data array; used to reset to page 1 when it changes. */
	data: readonly unknown[];
};

/**
 * Local Kendo Grid paging state (skip / take + page-change handler) for the
 * external Shipment Board, where the grid is not infinite-scrolled. Resets to
 * the first page whenever the underlying data array changes.
 *
 * Also persists itemsPerPage preference via useItemsPerPagePreference.
 */
export const useExternalPaging = ({
	enabled,
	data,
}: UseExternalPagingOptions) => {
	const {
		itemsPerPage,
		updateItemsPerPage,
		isLoading: isLoadingPreference,
	} = useItemsPerPagePreference(GridIdentifiers["Shipment Board"]);

	const [skip, setSkip] = useState(0);
	const [take, setTake] = useState(itemsPerPage);

	// Once the user manually changes the page size, the local `take` becomes the
	// source of truth. Without this guard, the async preference round-trip (or a
	// stale PUT response) would re-emit the previous itemsPerPage and overwrite
	// the user's selection.
	const hasUserSetTake = useRef(false);

	// Sync take with itemsPerPage when the preference first loads (before any user interaction).
	useEffect(() => {
		if (!hasUserSetTake.current) {
			setTake(itemsPerPage);
		}
	}, [itemsPerPage]);

	// Reset to page 1 when data changes (external).
	useEffect(() => {
		if (!enabled) return;
		setSkip(0);
	}, [data, enabled]);

	const handlePageChange = useCallback(
		(event: GridPageChangeEvent) => {
			// Prevent page changes while the preference is still loading
			if (isLoadingPreference) return;

			setSkip(event.page.skip);
			let nextTake = event.page.take;

			// Handle "All" special case
			if (typeof nextTake === "string" && nextTake === "All") {
				nextTake = data.length;
			} else {
				// Convert string to number if needed (Kendo may pass string values)
				if (typeof nextTake === "string") {
					const parsed = Number(nextTake);
					nextTake = Number.isNaN(parsed) ? 25 : parsed;
				}
				// Only persist when the page SIZE changed. Next/Previous navigation
				// keeps the same `take`, so writing here would schedule a redundant
				// full-preference PUT.
				if (nextTake !== take) {
					hasUserSetTake.current = true;
					updateItemsPerPage(nextTake);
				}
			}

			setTake(nextTake);
		},
		[data.length, updateItemsPerPage, isLoadingPreference, take],
	);

	return { skip, take, handlePageChange, isLoadingPreference };
};
